import React from "react";
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Modal,
  Skeleton,
  Space,
  Typography,
  message,
} from "antd";
import type { HospedeSolicitacaoReserva } from "../../../services/solicitacaoReservaService";
import { formatDate as formatCivilDate } from "../../../uteis/dateUtils";

const { Text } = Typography;

type Props = {
  open: boolean;
  loading: boolean;
  hospedes: HospedeSolicitacaoReserva[];
  protocolo?: string | null;
  onClose: () => void;
};

const formatCpf = (value?: string | null) => {
  const digits = (value ?? "").replace(/\D/g, "");

  if (digits.length !== 11) {
    return value || "—";
  }

  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
};

const getNomeCompleto = (hospede: HospedeSolicitacaoReserva) =>
  [hospede.nome, hospede.sobrenome].filter(Boolean).join(" ").trim() || "—";

const renderCopyableValue = (value: string) => (
  <Text copyable={value !== "—" ? { text: value } : false}>{value}</Text>
);

const getHospedeCopyText = (
  hospede: HospedeSolicitacaoReserva,
  index: number
) => {
  const linhas = [
    `Hóspede ${index + 1}`,
    `Nome: ${getNomeCompleto(hospede)}`,
    `CPF: ${formatCpf(hospede.cpf)}`,
    `Data de nascimento: ${
      hospede.dataNascimento ? formatCivilDate(hospede.dataNascimento) : "—"
    }`,
    `Titular: ${hospede.titular ? "Sim" : "Não"}`,
  ];

  if (hospede.pessoaId) {
    linhas.push(`Pessoa ID: ${hospede.pessoaId}`);
  }

  return linhas.join("\n");
};

export default function ModalHospedesSolicitacao({
  open,
  loading,
  hospedes,
  protocolo,
  onClose,
}: Props) {
  const handleCopy = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(successMessage);
    } catch (error) {
      console.error("Erro ao copiar dados dos hóspedes:", error);
      message.error("Não foi possível copiar os dados.");
    }
  };

  const handleCopyAll = () => {
    const texto = hospedes
      .map((hospede, index) => getHospedeCopyText(hospede, index))
      .join("\n\n");

    handleCopy(texto, "Dados dos hóspedes copiados.");
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      title={protocolo ? `Hóspedes da solicitação ${protocolo}` : "Hóspedes"}
      destroyOnClose
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : hospedes.length === 0 ? (
        <Empty
          description="Nenhum hóspede cadastrado para esta solicitação"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <Text type="secondary">
              {hospedes.length} hóspede(s) cadastrado(s)
            </Text>
            <Button onClick={handleCopyAll}>Copiar todos</Button>
          </div>

          {hospedes.map((hospede, index) => (
            <Card
              key={`${hospede.pessoaId}-${index}`}
              size="small"
              title={`Hóspede ${index + 1}${hospede.titular ? " • Titular" : ""}`}
            >
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Nome">
                  {renderCopyableValue(getNomeCompleto(hospede))}
                </Descriptions.Item>
                <Descriptions.Item label="CPF">
                  {renderCopyableValue(formatCpf(hospede.cpf))}
                </Descriptions.Item>
                <Descriptions.Item label="Data de nascimento">
                  {renderCopyableValue(
                    hospede.dataNascimento
                      ? formatCivilDate(hospede.dataNascimento)
                      : "—"
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Titular">
                  {renderCopyableValue(hospede.titular ? "Sim" : "Não")}
                </Descriptions.Item>
                <Descriptions.Item label="Pessoa ID">
                  {renderCopyableValue(
                    hospede.pessoaId ? String(hospede.pessoaId) : "—"
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ))}
        </Space>
      )}
    </Modal>
  );
}
